import React, { useMemo, useState } from "react";
import {
    Box,
    Typography,
    Paper,
    Stack,
    Chip,
    TextField,
    InputAdornment,
    Divider,
    IconButton,
    Avatar,
    Button,
    Menu,
    MenuItem,
} from "@mui/material";

import SearchIcon from "@mui/icons-material/Search";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ChatOutlinedIcon from "@mui/icons-material/ChatOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";

import { mockConversations } from "../../mocks/mockConversations";

/* =====================
   Helpers
===================== */

function initials(name = "") {
    return name
        .split(" ")
        .filter(Boolean)
        .slice(0, 2)
        .map((p) => p[0]?.toUpperCase())
        .join("");
}

function minutesDiff(date) {
    return Math.floor((Date.now() - new Date(date)) / 60000);
}

/* =====================
   Chips
===================== */

function PriorityChip({ priority }) {
    if (priority === "Alta") return <Chip label="Alta" color="error" size="small" />;
    if (priority === "Média") return <Chip label="Média" color="warning" size="small" />;
    return (
        <Chip
            label="Baixa"
            size="small"
            sx={{ fontWeight: 800, border: "1px solid var(--border)", bgcolor: "var(--branco)" }}
        />
    );
}

function StatusChip({ status }) {
    if (status === "Atrasada") return <Chip label="Atrasada" color="error" size="small" />;
    if (status === "Concluída") return <Chip label="Concluída" color="success" size="small" />;
    return <Chip label="Pendente" color="warning" size="small" />;
}

/* =====================
   Row
===================== */

function TaskRow({ task, onDone, onOpenChat }) {
    const [anchorEl, setAnchorEl] = useState(null);
    const openMenu = Boolean(anchorEl);

    const isLate = task.status === "Atrasada";

    return (
        <Box sx={{ px: 2, py: 1.6 }}>
            <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="space-between">
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
                    <Avatar
                        sx={{
                            width: 42,
                            height: 42,
                            bgcolor: "rgba(33,49,89,0.12)",
                            color: "var(--cise-blue)",
                            fontWeight: 900,
                        }}
                    >
                        {initials(task.leadName)}
                    </Avatar>

                    <Box sx={{ minWidth: 0 }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Typography fontWeight={900} noWrap>
                                {task.title}
                            </Typography>
                            <PriorityChip priority={task.priority} />
                            <StatusChip status={task.status} />
                        </Stack>

                        <Typography sx={{ fontSize: 13, color: "var(--cise-text-muted)" }} noWrap>
                            {task.leadName} • {task.owner}
                        </Typography>

                        {task.note && (
                            <Typography sx={{ fontSize: 13 }} noWrap>
                                “{task.note}”
                            </Typography>
                        )}
                    </Box>
                </Stack>

                <Stack direction="row" spacing={1} alignItems="center">
                    <Chip
                        icon={isLate ? <WarningAmberOutlinedIcon /> : <AccessTimeOutlinedIcon />}
                        label={task.due}
                        size="small"
                        sx={{
                            fontWeight: 900,
                            border: "1px solid var(--border)",
                            bgcolor: isLate ? "rgba(239,68,68,0.10)" : "var(--bg)",
                            color: isLate ? "var(--cise-error)" : "var(--cise-text)",
                        }}
                    />

                    <IconButton size="small" onClick={() => onDone(task.id)}>
                        <CheckCircleOutlineIcon fontSize="small" />
                    </IconButton>

                    <IconButton size="small" onClick={() => onOpenChat(task.leadId)}>
                        <ChatOutlinedIcon fontSize="small" />
                    </IconButton>

                    <IconButton size="small" onClick={(e) => setAnchorEl(e.currentTarget)}>
                        <MoreVertIcon fontSize="small" />
                    </IconButton>

                    <Menu
                        anchorEl={anchorEl}
                        open={openMenu}
                        onClose={() => setAnchorEl(null)}
                        PaperProps={{ sx: { borderRadius: 2 } }}
                    >
                        <MenuItem onClick={() => setAnchorEl(null)}>Editar</MenuItem>
                        <MenuItem onClick={() => setAnchorEl(null)}>Reagendar</MenuItem>
                        <MenuItem onClick={() => setAnchorEl(null)}>Excluir</MenuItem>
                    </Menu>
                </Stack>
            </Stack>
        </Box>
    );
}

/* =====================
   Page
===================== */

export default function TasksPage() {
    const [filter, setFilter] = useState("Hoje");
    const [q, setQ] = useState("");
    const [doneIds, setDoneIds] = useState([]);

    const tasks = useMemo(() => {
        return mockConversations.flatMap((c) => {
            const list = [];

            // sem resposta
            if (!c.responded) {
                const mins = minutesDiff(c.lastMessageAt);
                list.push({
                    id: `t_${c.id}_reply`,
                    title: "Responder lead",
                    leadId: c.id,
                    leadName: c.name,
                    owner: c.owner,
                    priority: mins >= 60 ? "Alta" : "Média",
                    due: mins >= 60 ? "Atrasada" : "Hoje",
                    status: mins >= 60 ? "Atrasada" : "Pendente",
                    note: c.lastUserMessage,
                });
            }

            // agendamento pendente
            if (c.appointment && c.appointment.status === "Pendente") {
                list.push({
                    id: `t_${c.id}_appointment`,
                    title: "Confirmar agendamento",
                    leadId: c.id,
                    leadName: c.name,
                    owner: c.owner,
                    priority: "Média",
                    due: `${c.appointment.date} ${c.appointment.time}`,
                    status: "Pendente",
                    note: c.appointment.notes,
                });
            }

            return list;
        });
    }, []);

    const filtered = useMemo(() => {
        const term = q.trim().toLowerCase();

        return tasks.filter((t) => {
            if (doneIds.includes(t.id)) return false;

            const matchText =
                !term ||
                t.title.toLowerCase().includes(term) ||
                t.leadName.toLowerCase().includes(term) ||
                t.owner.toLowerCase().includes(term);

            if (filter === "Atrasadas") return t.status === "Atrasada";
            if (filter === "Hoje") return t.status === "Pendente";
            if (filter === "Todas") return true;

            return matchText;
        });
    }, [tasks, q, filter, doneIds]);

    const markDone = (id) => setDoneIds((p) => [...p, id]);

    const openChat = (leadId) => {
        window.location.assign(`/admin/conversas?open=${leadId}`);
    };

    const pending = filtered.length;
    const late = filtered.filter((t) => t.status === "Atrasada").length;

    return (
        <Box p={2}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-end" sx={{ mb: 2 }}>
                <Box>
                    <Typography variant="h4" sx={{ fontWeight: 900, color: "var(--cise-blue)" }}>
                        Tarefas
                    </Typography>
                    <Typography sx={{ color: "var(--cise-text-muted)" }}>
                        Follow-ups e pendências do SDR
                    </Typography>
                </Box>

                <Chip
                    label={`${pending} pendentes • ${late} atrasadas`}
                    sx={{
                        border: "1px solid var(--border)",
                        bgcolor: "var(--branco)",
                        fontWeight: 900,
                    }}
                />
            </Stack>

            <Paper elevation={0} sx={{ p: 1.5, border: "1px solid var(--border)", mb: 2 }}>
                <Stack spacing={1.5}>
                    <TextField
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Buscar tarefa, lead, responsável..."
                        size="small"
                        fullWidth
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" />
                                </InputAdornment>
                            ),
                        }}
                    />

                    <Stack direction="row" spacing={1}>
                        {["Hoje", "Atrasadas", "Todas"].map((f) => (
                            <Chip
                                key={f}
                                label={f}
                                onClick={() => setFilter(f)}
                                sx={{
                                    fontWeight: 900,
                                    border: "1px solid var(--border)",
                                    bgcolor: filter === f ? "rgba(33,49,89,0.10)" : "var(--branco)",
                                }}
                            />
                        ))}
                    </Stack>
                </Stack>
            </Paper>

            <Paper elevation={0} sx={{ border: "1px solid var(--border)" }}>
                <Box sx={{ px: 2, py: 1.4, bgcolor: "var(--bg)" }}>
                    <Typography fontWeight={900}>{filter}</Typography>
                </Box>

                <Divider />

                {filtered.map((task, i) => (
                    <React.Fragment key={task.id}>
                        <TaskRow task={task} onDone={markDone} onOpenChat={openChat} />
                        {i !== filtered.length - 1 && <Divider />}
                    </React.Fragment>
                ))}

                {filtered.length === 0 && (
                    <Box sx={{ p: 3, textAlign: "center" }}>
                        <Typography fontWeight={900} color="var(--cise-blue)">
                            Nada por aqui
                        </Typography>
                        <Typography sx={{ color: "var(--cise-text-muted)" }}>
                            Ajuste os filtros ou conclua tarefas.
                        </Typography>
                    </Box>
                )}
            </Paper>
        </Box>
    );
}
